'use client';

import { useHasConsent } from '@/lib/tracking/consent/ConsentProvider';
import { gtmLoadingState } from '@/lib/tracking/gtm-loaded';
import { getGTMEnvironment } from '@/lib/tracking/helpers';
import { getChannelDomain, type Channel } from '@/lib/utils/channels';
import Script from 'next/script';
import { useEffect, useState } from 'react';

function getScriptUrl(channel: Channel) {
  const { host, tld } = getChannelDomain(channel);
  return `https://sgtm.${host + '.' + tld}/gtm.js?id=GTM-5FT2B8WR&l=dataLayer`;
}

function buildGTMDefaultsScript() {
  return `
    (function() {
      window.dataLayer = window.dataLayer || [];
      function gtag() { dataLayer.push(arguments); }
      gtag(
        'consent',
        'default',
        {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          functionality_storage: 'denied',
          wait_for_update: 500,
        }
      );
      window.dataLayer.push({ environment: "${getGTMEnvironment()}" });
    })();
  `;
}

function buildGTMInitScript() {
  return `
    (function() {
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    })();
  `;
}

export function GTMScript({ channel }: { channel: Channel }) {
  const [hasInitialPageLoadFired, setHasInitialPageLoadFired] = useState(false);
  const scriptUrl = getScriptUrl(channel);
  const canLoad = useHasConsent('analytics');

  useEffect(() => {
    const unsubscribe = gtmLoadingState.subscribe(
      ({ initialPageLoadFired }) => {
        if (initialPageLoadFired) {
          setHasInitialPageLoadFired(true);
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return scriptUrl ? (
    <>
      {/* prod | staging | dev */}
      <Script
        id='_gtm-init'
        dangerouslySetInnerHTML={{
          __html: buildGTMDefaultsScript(),
        }}
      />

      {canLoad && hasInitialPageLoadFired && (
        <>
          <Script
            id='_gtm-start'
            type='text/javascript'
            dangerouslySetInnerHTML={{
              __html: buildGTMInitScript(),
            }}
          />
          <Script
            async={true}
            id='_gtm-load'
            type='text/javascript'
            src={scriptUrl}
            onLoad={gtmLoadingState.setGTMLoaded}
          />
        </>
      )}
    </>
  ) : null;
}
